import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Clock, Star, ArrowLeft, Mail } from 'lucide-react';

const PricingPolicy = () => {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary-600 via-accent-600 to-primary-700 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">Pricing Policy</h1>
          <p className="text-xl text-primary-100">Full details on plans, setup fees, minutes, guarantees and what's included</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/pricing" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-10 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Link>

          {/* Plans Overview */}
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">Plans &amp; Monthly Fees</h2>
          <p className="text-gray-600 mb-8">All plans include a bespoke, custom-built Voice AI agent and a guaranteed number of call minutes per month.</p>

          <div className="overflow-x-auto mb-12">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-primary-600 to-accent-600 text-white">
                  <th className="text-left px-5 py-3 rounded-tl-xl font-semibold">Plan</th>
                  <th className="text-left px-5 py-3 font-semibold">Monthly Fee</th>
                  <th className="text-left px-5 py-3 font-semibold">Included Minutes</th>
                  <th className="text-left px-5 py-3 font-semibold">Overage Rate</th>
                  <th className="text-left px-5 py-3 rounded-tr-xl font-semibold">Setup Fee</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'Starter', monthly: '£97', minutes: '150 mins', overage: '£0.35/min', setup: '£1,000' },
                  { name: 'Growth', monthly: '£147', minutes: '400 mins', overage: '£0.22/min', setup: '£1,200' },
                  { name: 'Scale', monthly: '£247', minutes: '1,000 mins', overage: '£0.18/min', setup: '£1,500' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-5 py-4 font-semibold text-gray-900">{row.name}</td>
                    <td className="px-5 py-4 text-gray-700">{row.monthly}</td>
                    <td className="px-5 py-4 text-gray-700">{row.minutes}</td>
                    <td className="px-5 py-4 text-gray-700">{row.overage}</td>
                    <td className="px-5 py-4 text-gray-700">{row.setup}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* What's Included */}
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">What's Included in Every Plan</h2>
          <div className="bg-gray-50 rounded-2xl p-6 mb-12">
            <ul className="space-y-3">
              {[
                'Bespoke custom-built Voice AI agent tailored to your business',
                'Agreed number of included call minutes per month (resets on the 1st)',
                'Flat monthly retainer — no hidden fees',
                'Full client satisfaction guarantee',
                'Written brief agreement before any payment is taken',
                'Build & internal testing by MMI Online (typically 5–10 business days)',
                'Client review of prompt and call flow documents before go-live',
                'Monthly retainer begins only from your go-live date — not before',
                'Upfront discount: pay 6 months in advance and receive 1 month free',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* How It Works */}
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">How It Works</h2>
          <div className="space-y-5 mb-12">
            {[
              {
                icon: Clock,
                title: 'How minutes work',
                body: 'Your included minutes cover the total call time handled by your Voice AI agent each month. If your agent handles 200 minutes of calls and your plan includes 150, you are charged for the additional 50 minutes at your plan\'s overage rate. Minutes reset on the 1st of each month.',
              },
              {
                icon: Shield,
                title: 'Satisfaction guarantee',
                body: 'We will never keep your money if our product does not work as promised. If the agent fails to meet the agreed brief due to a technical, script, or integration failure, your setup fee is refunded within 14 business days.',
              },
              {
                icon: Star,
                title: 'Upfront discount',
                body: 'Pay 6 months in advance and receive 1 month free. Monthly retainer begins on your go-live date — not before.',
              },
            ].map(({ icon: Icon, title, body }, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Client Guarantee & Protection Policy */}
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">Client Guarantee &amp; Protection Policy</h2>
          <p className="text-gray-600 mb-8">
            Both sides are guaranteed fair treatment. Clients are protected against paying for a product that does not perform. MMI Online is protected against investing significant build time without genuine commitment.
          </p>

          <div className="rounded-2xl border-2 border-gray-200 p-8 bg-gray-50 mb-10">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Satisfaction Guarantee</h3>
              <p className="text-gray-700 text-sm mb-4">
                See the completed, tested agent before fully committing. Pay the full standard setup fee once satisfied.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Full standard setup fee applies</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> See the working agent before go-live</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Full satisfaction guarantee in place</li>
              </ul>
              <p className="text-xs text-gray-500 mt-4">
                <em>A minimum 30-day live period applies after go-live before any refund can be exercised. Refund right applies only where the agent fails to meet agreed KPIs during that period.</em>
              </p>
            </div>

          {/* Process steps */}
          <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-8 text-white mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center">How the Process Works</h3>
            <ol className="space-y-4">
              {[
                ['Brief agreed', 'Before any payment is taken, MMI Online and the client agree a written brief covering call flows, responses, and success criteria.'],
                ['Payment taken', 'The setup fee is taken before build begins.'],
                ['Build & testing', 'MMI Online builds and internally tests the agent against the agreed brief. Typically 5–10 business days.'],
                ['Client review', 'You review the first draft of the agent\'s prompt and call flow documents. Revisions are incorporated and sign-off is required before the build proceeds.'],
                ['Go-live', 'On client approval, the agent goes live. The monthly retainer begins from this date.'],
                ['Guarantee period', '30-day guarantee from go-live. Refund right applies where the agent fails to meet agreed KPIs during that period.'],
              ].map(([step, desc], i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <div>
                    <span className="font-semibold">{step}: </span>
                    <span className="text-white/85 text-sm">{desc}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Contact */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <p className="text-gray-700 font-semibold">MMI Online</p>
            <p className="text-gray-600">Email: contact@mmionline.online</p>
            <p className="text-gray-600">Phone our Voice AI bot: 07427 860249</p>
          </div>

          <div className="flex items-center justify-center space-x-4 mt-12 pt-8 border-t border-gray-200">
            <Link to="/pricing" className="text-primary-600 hover:text-primary-700 font-medium">
              View Pricing Plans
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/contact" className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium">
              <Mail className="w-4 h-4 mr-2" />
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPolicy;
